import React from 'react';

function AiPhoneAgentsArticle() {
  return (
    <div>
      <p>
        If you run a restaurant in Los Angeles, you already know the drill: the phone
        rings off the hook during the dinner rush, nobody can pick up, and half those
        callers never call back. Meanwhile, you're paying someone $20+ an hour just to
        answer the phone during the day — and after hours? Those calls go straight to
        voicemail, and most people hang up.
      </p>
      <p>
        That's money walking out the door every single day. On average, our restaurant
        clients save $17,000 a year by letting AI handle their phones. Here's how it works
        in plain English.
      </p>

      <h2>What You're Spending Now</h2>
      <p>
        Let's be honest about what phone staffing actually costs a typical LA restaurant:
      </p>
      <ul>
        <li><strong>A host or receptionist answering calls:</strong> $17-20/hour in Los Angeles</li>
        <li><strong>That adds up to:</strong> $44,000-$62,000 a year for one full-time person</li>
        <li><strong>After-hours calls:</strong> Another $8,000-$12,000/year if you use an answering service</li>
        <li><strong>Missed calls during rush:</strong> About 30% of calls go unanswered when you're slammed</li>
      </ul>
      <p>
        And here's the part that hurts most — every missed reservation call is about $85
        in lost revenue. If you're missing just 5 calls a day during busy periods, that's
        over $150,000 a year in potential business you're leaving on the table.
      </p>

      <h2>What Changes with AI</h2>
      <p>
        With an AI phone agent, every single call gets answered — immediately. No hold
        music, no "please leave a message," no missed opportunities. Here's what it does:
      </p>
      <ul>
        <li><strong>Answers in under 2 seconds</strong> — faster than any human can pick up</li>
        <li><strong>Works 24/7</strong> — weekends, holidays, 2 AM, doesn't matter</li>
        <li><strong>Handles the basics:</strong> Reservations, menu questions, hours, directions, catering inquiries, dietary questions</li>
        <li><strong>Transfers when needed:</strong> If someone needs to talk to a real person, the AI connects them right away</li>
        <li><strong>Speaks English and Spanish</strong> — important for many LA neighborhoods</li>
      </ul>
      <p>
        The AI sounds natural and friendly — not robotic. Most callers don't even realize
        they're talking to a computer. And 94% of callers say they're satisfied with the
        experience.
      </p>

      <h2>The Actual Savings</h2>
      <p>
        Here's how the math works out for the average LA restaurant, based on 40+ real
        deployments:
      </p>
      <ul>
        <li><strong>Less phone staffing needed:</strong> Save $14,000/year</li>
        <li><strong>Recovered missed reservations:</strong> $8,500/year from calls that used to go to voicemail</li>
        <li><strong>Fewer no-shows:</strong> $3,200/year — the AI sends automatic confirmation calls that cut no-shows by 40%</li>
        <li><strong>Cost of the AI service:</strong> -$8,700/year</li>
        <li><strong>Your net savings:</strong> About $17,000/year</li>
      </ul>
      <p>
        That's real money back in your pocket — or money you can put toward better
        ingredients, staff raises, or that kitchen upgrade you've been putting off.
      </p>

      <h2>What the Setup Looks Like</h2>
      <p>
        This is the part where most restaurant owners expect a headache. It's not.
        Here's what happens:
      </p>
      <ol>
        <li><strong>Week 1:</strong> We learn your menu, your hours, how you handle reservations (OpenTable, Resy, or just a notebook — all fine), and how you want calls handled. We set everything up.</li>
        <li><strong>Week 2:</strong> We test it with real calls, train your staff on the simple dashboard (it's really just a screen that shows who called and what they wanted), and fine-tune the responses.</li>
      </ol>
      <p>
        That's it. Two weeks. You don't need to install anything, learn any software, or
        change how you run your restaurant. The AI just starts answering your phone.
      </p>

      <h2>You Don't Have to Be Techy</h2>
      <p>
        We built this specifically for business owners who don't have time to mess with
        technology. You don't need a computer science degree. You don't even need to be
        particularly good with your phone. We handle the tech, you handle the cooking.
      </p>
      <p>
        Most of our restaurant clients pay for the service within 90 days just from the
        reservations they would have otherwise missed.
      </p>

      <h2>Ready to Stop Missing Calls?</h2>
      <p>
        If your restaurant handles 50 or more calls a day and you're tired of paying
        someone just to answer the phone, this is probably a good fit. We work with
        restaurants all over Los Angeles — from taco shops in East LA to fine dining
        in Santa Monica.
      </p>
      <p>
        <strong>Call our AI receptionist at (424) 434-4541</strong> or visit{' '}
        <a href="https://ubotika.com/#contact">ubotika.com</a> to schedule a free
        consultation. We'll show you exactly what it would look like for your restaurant
        — no pressure, no tech jargon, just a straight answer.
      </p>
    </div>
  );
}

export default AiPhoneAgentsArticle;
